import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/shared/ui/theme/ThemeProvider';
import { EnrollmentScreenLayout } from '@/presentation/layouts/EnrollmentScreenLayout';
import { useEnrollPinMutation } from '@/modules/admin/application/enrollMutations';
import { useKeypadInput } from '@/modules/attendance/presentation/hooks/useKeypadInput';
import { enrollBiometricLocally } from '@/modules/admin/application/use-cases/enrollBiometricLocallyUseCase';
import { isNetworkOrTimeoutError } from '@/shared/api/classifyApiError';
import { LocalEmployeeRepository } from '@/modules/employees/infrastructure/repositories/LocalEmployeeRepository';
import { TEMPLATE_PIN } from '@/modules/employees/domain/biometricConstants';
import type { BiometricDataDTO } from '@/modules/employees/infrastructure/dto/EmployeeDTO';

type Step = 'set' | 'confirm' | 'done' | 'error';

export function EnrollPinPage() {
  const { C, T } = useTheme();
  const navigate = useNavigate();
  const { idEntity } = useParams<{ idEntity: string }>();
  const { t } = useTranslation('common');

  const [step, setStep] = useState<Step>('set');
  const [pin, setPin] = useState('');
  const [pin2, setPin2] = useState('');
  const [err, setErr] = useState('');
  const [apiErr, setApiErr] = useState('');

  const { mutateAsync: enrollPin, isPending } = useEnrollPinMutation();
  const queryClient = useQueryClient();

  const current = step === 'set' ? pin : pin2;
  const setCurrent = step === 'set' ? setPin : setPin2;

  const doEnroll = useCallback(async (confirmedPin: string) => {
    if (!idEntity) return;
    const idEntityNum = Number(idEntity);
    const repo = new LocalEmployeeRepository();

    try {
      // Offline-first: if the browser is offline, skip HTTP and enroll locally
      if (!navigator.onLine) {
        const employee = await repo.findByEntity(idEntityNum);
        if (!employee) {
          setApiErr('Empleado no disponible offline. Sincronice la lista primero.');
          setStep('error');
          return;
        }
        await enrollBiometricLocally({
          idPerson: employee.idPerson,
          idEntity: idEntityNum,
          biometric: { templateType: 'pin', pin: confirmedPin },
        });
        void queryClient.invalidateQueries({ queryKey: ['employees', 'lookup'] });
        setStep('done');
        return;
      }

      // Online path: call enrollment API
      await enrollPin({ idEntity: idEntityNum, pin: confirmedPin });

      // Patch local DB immediately for offline PIN availability (no need to wait for sync)
      const employee = await repo.findByEntity(idEntityNum);
      if (employee) {
        const entry: BiometricDataDTO = {
          id_template_biometric: TEMPLATE_PIN,
          biometric_data: [confirmedPin],
          nro_biometric: 1,
          id_entity: idEntityNum,
          id_entity_biometric: undefined,
          at_created: new Date().toISOString(),
          metadata: [],
        };
        await repo.patchBiometricData(employee.idPerson, entry);
      }

      void queryClient.invalidateQueries({ queryKey: ['employees', 'lookup'] });
      setStep('done');
    } catch (error: unknown) {
      // Network/timeout → fall through to offline enrollment path
      if (isNetworkOrTimeoutError(error)) {
        const employee = await repo.findByEntity(idEntityNum);
        if (!employee) {
          setApiErr('Empleado no disponible offline. Sincronice la lista primero.');
          setStep('error');
          return;
        }
        await enrollBiometricLocally({
          idPerson: employee.idPerson,
          idEntity: idEntityNum,
          biometric: { templateType: 'pin', pin: confirmedPin },
        });
        void queryClient.invalidateQueries({ queryKey: ['employees', 'lookup'] });
        setStep('done');
        return;
      }

      setApiErr((error as { message?: string })?.message ?? 'Error');
      setStep('error');
    }
  }, [idEntity, enrollPin, queryClient]);

  const press = useCallback((d: string) => {
    if (step === 'done' || step === 'error' || isPending) return;
    setErr('');
    if (current.length >= 4) return;
    const next = current + d;
    setCurrent(next);
    if (next.length === 4) {
      if (step === 'set') {
        setTimeout(() => { setStep('confirm'); }, 220);
      } else {
        setTimeout(() => {
          if (next === pin) {
            doEnroll(next);
          } else {
            setErr(t('enrollPin.errorMismatch'));
            setPin('');
            setPin2('');
            setStep('set');
          }
        }, 220);
      }
    }
  }, [step, isPending, current, pin, setCurrent, doEnroll, t]);

  const del = useCallback(() => {
    if (step === 'done' || step === 'error' || isPending) return;
    setCurrent(current.slice(0, -1));
  }, [step, isPending, current, setCurrent]);

  useKeypadInput({ onDigit: press, onDelete: del, enabled: !isPending && step !== 'done' && step !== 'error' });

  const title =
    step === 'set' ? t('enrollPin.stepSet') :
    step === 'confirm' ? t('enrollPin.stepConfirm') :
    step === 'done' ? t('enrollPin.stepDone') :
    t('enrollPin.stepError');

  const subtitle =
    step === 'set' ? t('enrollPin.hintSet') :
    step === 'confirm' ? t('enrollPin.hintConfirm') :
    step === 'done' ? '' :
    (apiErr || 'Ocurrió un error. Intenta de nuevo.');

  return (
    <EnrollmentScreenLayout
      onBack={() => { void navigate(-1); }}
      title={t('enrollPin.title')}
      badgeLabel={t('enrollPin.badge')}
      badgeColor={C.blue}
      T={T}
    >
      <div style={{ maxWidth: 340, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

          {/* Icon */}
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: step === 'done' ? `${C.success}18` : step === 'error' ? `${C.danger}18` : `${C.blue}14`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: step === 'done' ? C.success : step === 'error' ? C.danger : C.blue, marginBottom: 12 }}>
            {step === 'done' ? (
              <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            ) : step === 'error' ? (
              <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
              </svg>
            ) : (
              <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="10" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            )}
          </div>

          <div style={{ fontSize: 15, fontWeight: 700, color: T.clockText, marginBottom: 4, textAlign: 'center' }}>{title}</div>
          {subtitle && (
            <div style={{ fontSize: 11, color: step === 'error' ? C.danger : T.textMuted, marginBottom: 20, textAlign: 'center' }}>{subtitle}</div>
          )}

          {/* Keypad area */}
          {(step === 'set' || step === 'confirm') && (
            <>
              {/* Dots */}
              <div style={{ display: 'flex', gap: 12, marginBottom: 22 }}>
                {[0, 1, 2, 3].map((i) => (
                  <div
                    key={i}
                    style={{ width: 36, height: 44, borderRadius: 9, border: `1.5px solid ${err ? C.danger : current.length > i ? C.blue : T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', background: T.surfaceBg, fontSize: 18, fontWeight: 700, color: T.text }}
                  >
                    {current.length > i ? '●' : ''}
                  </div>
                ))}
              </div>
              {err && (
                <div style={{ fontSize: 10, color: C.danger, marginBottom: 14, textAlign: 'center' }}>{err}</div>
              )}

              {/* Keypad */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 60px)', gap: 10 }}>
                {(['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', 'del'] as const).map((k, i) => {
                  if (k === '') return <div key={i} />;
                  if (k === 'del') {
                    return (
                      <button
                        key={i}
                        onClick={del}
                        style={{ width: 60, height: 54, borderRadius: 11, background: 'transparent', border: `1px solid ${T.border}`, cursor: 'pointer', color: T.text, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >
                        <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                          <path d="M21 4H8l-7 8 7 8h13a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z" />
                          <line x1="18" y1="9" x2="12" y2="15" /><line x1="12" y1="9" x2="18" y2="15" />
                        </svg>
                      </button>
                    );
                  }
                  return (
                    <button
                      key={i}
                      onClick={() => { press(k); }}
                      style={{ width: 60, height: 54, borderRadius: 11, background: T.surfaceBg, border: `1px solid ${T.border}`, cursor: 'pointer', color: T.text, fontFamily: 'inherit', fontSize: 18, fontWeight: 700 }}
                    >
                      {k}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* Sending spinner */}
          {isPending && (
            <div style={{ marginTop: 24, fontSize: 11, color: T.textMuted }}>{t('enrollPin.saving')}</div>
          )}

          {/* Done: back button */}
          {step === 'done' && (
            <button
              onClick={() => { void navigate(-1); }}
              style={{ marginTop: 28, padding: '11px 32px', borderRadius: 11, border: 'none', background: C.success, color: '#fff', fontFamily: 'inherit', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
            >
              {t('enrollPin.back')}
            </button>
          )}

          {/* Error: retry button */}
          {step === 'error' && (
            <button
              onClick={() => { setPin(''); setPin2(''); setApiErr(''); setErr(''); setStep('set'); }}
              style={{ marginTop: 28, padding: '11px 32px', borderRadius: 11, border: 'none', background: C.primary, color: '#fff', fontFamily: 'inherit', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
            >
              {t('enrollPin.retry')}
            </button>
          )}
        </div>

      {/* Test-only trigger — never visible in production (display:none) */}
      <button
        data-testid="__test-pin-submit__"
        style={{ display: 'none' }}
        onClick={(e) => {
          const val = (e.currentTarget as HTMLButtonElement).dataset['value'] ?? '';
          void doEnroll(val);
        }}
      />
    </EnrollmentScreenLayout>
  );
}
